/**
 * Opponent Arrow — off-screen indicator showing opponent direction in ascent mode.
 *
 * When the opponent is outside the camera viewport, renders a player-colored
 * arrow at the viewport edge pointing toward them. Pulses subtly.
 */

import { Container, Graphics } from 'pixi.js';
import type { Camera } from 'bonkjs';
import { WORLD } from '../constants';
import type { GameState } from '../types';
import { modeIsAscent } from '../game/mode';

const ARROW_SIZE = 24;
const EDGE_MARGIN = 40; // px from screen edge (in canvas coords)
const PULSE_SPEED = 3;  // radians per second

export class OpponentArrowRenderer {
  private gfx: Graphics;
  private uiContainer: Container;
  private pulsePhase = 0;

  constructor(uiContainer: Container) {
    this.uiContainer = uiContainer;
    this.gfx = new Graphics();
    this.gfx.visible = false;
    uiContainer.addChild(this.gfx);
  }

  update(state: GameState, camera: Camera, dt: number): void {
    // Show arrows in ascent mode or on wide stages (players can be far off-screen)
    const isWideStage = WORLD.VIRTUAL_WIDTH > 2560;
    if (!modeIsAscent(state.modeState.mode) && !isWideStage) {
      this.gfx.visible = false;
      return;
    }
    if (state.phase !== 'aiming' && state.phase !== 'firing' && state.phase !== 'defensive_movement') {
      this.gfx.visible = false;
      return;
    }

    // Find opponent (the non-current player who is alive)
    const opponentIndex = state.currentPlayer === 0 ? 1 : 0;
    const opponent = state.players[opponentIndex];
    if (!opponent || !opponent.alive) {
      this.gfx.visible = false;
      return;
    }

    // Convert opponent world position to screen position
    const camPos = camera.getPosition();
    const zoom = camera.zoom;
    const vpW = WORLD.CANVAS_WIDTH;
    const vpH = WORLD.CANVAS_HEIGHT;
    const screenX = (opponent.x - camPos[0]) * zoom + vpW / 2;
    const screenY = (opponent.y - camPos[1]) * zoom + vpH / 2;

    // Check if opponent is on-screen (with some padding)
    const pad = 30;
    if (screenX >= pad && screenX <= vpW - pad && screenY >= pad && screenY <= vpH - pad) {
      this.gfx.visible = false;
      return;
    }

    // Opponent is off-screen — compute arrow position at viewport edge
    this.gfx.visible = true;
    this.pulsePhase += dt * PULSE_SPEED;

    // Direction from screen center to opponent screen position
    const cx = vpW / 2;
    const cy = vpH / 2;
    const dx = screenX - cx;
    const dy = screenY - cy;
    const angle = Math.atan2(dy, dx);

    // Find intersection with viewport edge
    const halfW = vpW / 2 - EDGE_MARGIN;
    const halfH = vpH / 2 - EDGE_MARGIN;

    // Ray-box intersection
    let t = Infinity;
    if (dx !== 0) {
      const tRight = halfW / Math.abs(dx);
      const tLeft = halfW / Math.abs(dx);
      t = Math.min(t, dx > 0 ? tRight : tLeft);
    }
    if (dy !== 0) {
      const tBottom = halfH / Math.abs(dy);
      const tTop = halfH / Math.abs(dy);
      t = Math.min(t, dy > 0 ? tBottom : tTop);
    }

    const arrowX = cx + dx * t;
    const arrowY = cy + dy * t;

    // Pulse scale
    const pulse = 1.0 + 0.15 * Math.sin(this.pulsePhase);
    const size = ARROW_SIZE * pulse;

    // Draw arrow triangle pointing toward opponent
    this.gfx.clear();
    this.gfx.position.set(arrowX, arrowY);
    this.gfx.rotation = angle;

    // Triangle pointing right (rotation handles direction)
    const tipX = size;
    const tipY = 0;
    const backL = -size * 0.6;
    const backW = size * 0.5;

    this.gfx.moveTo(tipX, tipY);
    this.gfx.lineTo(backL, -backW);
    this.gfx.lineTo(backL, backW);
    this.gfx.closePath();
    this.gfx.fill({ color: opponent.color, alpha: 0.85 });
    this.gfx.stroke({ width: 2, color: 0xffffff, alpha: 0.5 });
  }

  destroy(): void {
    this.gfx.destroy();
  }
}
